import { useEffect, useMemo, useState } from 'react';
import SEOHead from '@/components/SEOHead';
import BlogHeroMagazineV2 from '@/components/marketing/blog/v2/BlogHeroMagazineV2';
import BlogCategoryStripV2 from '@/components/marketing/blog/v2/BlogCategoryStripV2';
import BlogArticleGridV2 from '@/components/marketing/blog/v2/BlogArticleGridV2';
import NewsletterSectionV2 from '@/components/marketing/blog/v2/NewsletterSectionV2';

interface BlogEntry {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  tags: string[];
  image?: string;
  cover?: string;
}

export default function Blog() {
  const [articles, setArticles] = useState<BlogEntry[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    fetch('/blog/index.json')
      .then((r) => r.json())
      .then((data: BlogEntry[]) => setArticles(data))
      .catch(() => setArticles([]));
  }, []);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    articles.forEach((a) => {
      const cat = a.tags[0];
      if (!cat) return;
      counts.set(cat, (counts.get(cat) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 7)
      .map(([label, count]) => ({ label, count }));
  }, [articles]);

  const filtered = useMemo(() => {
    if (!activeCategory) return articles;
    return articles.filter((a) => a.tags[0] === activeCategory);
  }, [articles, activeCategory]);

  const featured = articles[0] ?? null;

  return (
    <>
      <SEOHead
        title="Blog — Conseils pour commerçants africains | Velko POS"
        description="Conseils, guides et astuces pour les commerçants en Afrique : logiciel de caisse, gestion de stock, Mobile Money, ventes hors ligne. Le blog Velko POS."
        path="/blog"
        breadcrumbs={[
          { name: 'Accueil', path: '/' },
          { name: 'Blog', path: '/blog' },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Blog Velko POS',
          description:
            'Conseils et guides pour les commerçants africains : gestion de boutique, Mobile Money, logiciel de caisse.',
          url: 'https://velko-pos.com/blog',
          publisher: {
            '@type': 'Organization',
            name: 'Velko',
          },
        }}
      />

      <BlogHeroMagazineV2 featured={featured} totalArticles={articles.length} />

      {articles.length > 0 && (
        <BlogCategoryStripV2
          categories={categories}
          total={articles.length}
          active={activeCategory}
          onSelect={setActiveCategory}
        />
      )}

      <BlogArticleGridV2 articles={filtered} />

      <NewsletterSectionV2 />
    </>
  );
}
